import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Checkbox,
    FormControlLabel
} from '@mui/material';
import MenuBookIcon from '@mui/icons-material/MenuBook';

import MicIcon from '@mui/icons-material/Mic';
import QuizIcon from '@mui/icons-material/Quiz';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BarChartIcon from '@mui/icons-material/BarChart';
import { useAuth } from '../context/AuthContext';

const DashboardWelcomeOverlay = () => {
    const { user } = useAuth();
    const [open, setOpen] = useState(false);
    const [doNotShowAgain, setDoNotShowAgain] = useState(false);
    const BASE_STORAGE_KEY = 'deepalert_dashboard_welcome_shown';

    useEffect(() => {
        if (!user || !user.email) return;

        // Check if user has seen the dashboard welcome overlay before (using specific email key)
        const storageKey = `${BASE_STORAGE_KEY}_${user.email}`;
        const hasSeenWelcome = localStorage.getItem(storageKey);

        if (!hasSeenWelcome) {
            console.log('DashboardWelcomeOverlay: User has not seen welcome. Opening...');
            setOpen(true);
        } else {
            console.log('DashboardWelcomeOverlay: User HAS seen welcome. Not opening.');
        }
    }, [user]);

    const handleClose = () => {
        setOpen(false);
        if (doNotShowAgain && user && user.email) {
            const storageKey = `${BASE_STORAGE_KEY}_${user.email}`;
            localStorage.setItem(storageKey, 'true');
        }
    };

    return (
        <Dialog
            open={open}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                }
            }}
            BackdropProps={{
                sx: {
                    backgroundColor: 'rgba(0, 0, 0, 0.75)',
                }
            }}
        >
            <DialogTitle sx={{
                bgcolor: 'primary.main',
                color: 'white',
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span role="img" aria-label="info" style={{ fontSize: '1.5rem' }}>ℹ️</span>
                    <Typography variant="h5" component="span" fontWeight="bold">
                        Your Course Dashboard
                    </Typography>
                </Box>
            </DialogTitle>

            <DialogContent sx={{ pt: 3, pb: 2 }}>
                <Typography variant="body1" paragraph>
                    Here's how to navigate your learning journey:
                </Typography>

                <List sx={{ py: 0 }}>
                    <ListItem sx={{ px: 0, py: 1 }}>
                        <ListItemIcon sx={{ minWidth: 40 }}>
                            <MenuBookIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText
                            primary={<Typography variant="subtitle1" fontWeight="600">TEXT BOOK</Typography>}
                            secondary="Contains embedded videos, tips and examples."
                        />
                    </ListItem>



                    <ListItem sx={{ px: 0, py: 1 }}>
                        <ListItemIcon sx={{ minWidth: 40 }}>
                            <MicIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText
                            primary={<Typography variant="subtitle1" fontWeight="600">PODCAST</Typography>}
                            secondary="Listen to the audio version - perfect for learning on the go."
                        />
                    </ListItem>

                    <ListItem sx={{ px: 0, py: 1 }}>
                        <ListItemIcon sx={{ minWidth: 40 }}>
                            <QuizIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText
                            primary={<Typography variant="subtitle1" fontWeight="600">CERTIFICATION EXAM</Typography>}
                            secondary="Test your knowledge. You must score 85% to earn your certificate. You get 3x attempts per day to try a failed exam."
                        />
                    </ListItem>

                    <ListItem sx={{ px: 0, py: 1 }}>
                        <ListItemIcon sx={{ minWidth: 40 }}>
                            <BarChartIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText
                            primary={<Typography variant="subtitle1" fontWeight="600">COMPLEXITY</Typography>}
                            secondary="Some courses are more complex (difficult) than others. Basic (Bronze), Intermediate (Silver), Specialist (Gold)."
                        />
                    </ListItem>

                    <ListItem sx={{ px: 0, py: 1 }}>
                        <ListItemIcon sx={{ minWidth: 40 }}>
                            <CheckCircleIcon sx={{ color: 'success.main' }} />
                        </ListItemIcon>
                        <ListItemText
                            primary={<Typography variant="subtitle1" fontWeight="600">COURSE COMPLETED</Typography>}
                            secondary="Completed courses will be indicated in green and will contain a Certificate Download button."
                        />
                    </ListItem>
                </List>


            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 3, flexDirection: 'column', alignItems: 'stretch', gap: 1 }}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={doNotShowAgain}
                            onChange={(e) => setDoNotShowAgain(e.target.checked)}
                            color="primary"
                        />
                    }
                    label="Do not show this again"
                    sx={{ alignSelf: 'flex-start', ml: 0.5 }}
                />
                <Button
                    onClick={handleClose}
                    variant="contained"
                    size="large"
                    fullWidth
                    sx={{
                        py: 1.5,
                        fontSize: '1rem',
                        fontWeight: 'bold',
                        textTransform: 'none'
                    }}
                >
                    Got it — Let's Begin
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default DashboardWelcomeOverlay;
